import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, MapPin } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { useLanguage } from "../i18n/LanguageContext";

// FormSubmit — fully static, no backend required.
// Submissions go straight to the email below. The first submission triggers an
// activation email sent by FormSubmit; click the activation link in that email
// (sent to the address below) once to enable all future submissions.
const FORMSUBMIT_EMAIL = "cesarblee2@gmail.com";
const FORMSUBMIT_ENDPOINT = `https://formsubmit.co/ajax/${FORMSUBMIT_EMAIL}`;

export const QuoteForm = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.address || !form.service) {
      toast.error(t.quote.errorTitle, {
        description: "Please fill in all required fields.",
      });
      return;
    }
    setSubmitting(true);
    try {
      // Build payload from the actual form element so the hidden FormSubmit
      // configuration inputs (_captcha, _subject, etc.) are picked up.
      const fd = new FormData(e.currentTarget);
      // Ensure the submitted state always reflects the React state values.
      fd.set("Name", form.name);
      fd.set("Email", form.email);
      fd.set("Phone", form.phone);
      fd.set("Address", form.address);
      fd.set("Service", form.service);
      fd.set("Message", form.message || "(none)");
      fd.set("_subject", `New quote request — ${form.name} (${form.service})`);
      fd.set("_replyto", form.email);

      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      const ok = res.ok && (data.success === "true" || data.success === true);
      if (ok) {
        toast.success(t.quote.successTitle, {
          description: t.quote.successBody,
        });
        setForm({
          name: "",
          email: "",
          phone: "",
          address: "",
          service: "",
          message: "",
        });
      } else {
        throw new Error(data.message || "FormSubmit error");
      }
    } catch (err) {
      console.error(err);
      toast.error(t.quote.errorTitle, { description: t.quote.errorBody });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="quote"
      data-testid="quote-section"
      className="py-24 px-6 md:px-12 bg-dark-section grain-overlay"
    >
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5 text-on-dark">
          <span className="text-[11px] uppercase tracking-[0.25em] font-bold text-[#E8A58B]">
            {t.quote.overline}
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium">
            {t.quote.title}
          </h2>
          <p className="mt-6 text-white/75 leading-relaxed max-w-md">
            {t.quote.subtitle}
          </p>
          <div className="mt-10 border-t border-white/10 pt-8 space-y-5 text-sm">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/50">
                {t.contact.phoneLabel}
              </p>
              <a
                href="tel:+14388743855"
                data-testid="quote-phone-link"
                className="mt-1 block font-display text-xl hover:text-[#E8A58B] transition-colors"
              >
                +1 (438) 874-3855
              </a>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-white/50">
                {t.contact.addressLabel}
              </p>
              <p className="mt-1 text-white/90 leading-relaxed">
                {t.contact.address}
              </p>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="quote-form"
          className="lg:col-span-7 bg-[#FDFBF7] p-8 md:p-10 space-y-5"
        >
          {/* FormSubmit configuration */}
          <input type="hidden" name="_captcha" value="false" />
          <input type="hidden" name="_template" value="table" />
          <input type="hidden" name="_subject" value="New quote request — Cesar's Repairs" />
          <input type="text" name="_honey" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="q-name" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {t.quote.labels.name}
              </Label>
              <Input
                id="q-name"
                data-testid="quote-input-name"
                type="text"
                required
                placeholder={t.quote.placeholders.name}
                value={form.name}
                onChange={update("name")}
                className="mt-2 rounded-none border-earth focus-visible:ring-[#2A5A3B]"
              />
            </div>
            <div>
              <Label htmlFor="q-email" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {t.quote.labels.email}
              </Label>
              <Input
                id="q-email"
                data-testid="quote-input-email"
                type="email"
                required
                placeholder={t.quote.placeholders.email}
                value={form.email}
                onChange={update("email")}
                className="mt-2 rounded-none border-earth focus-visible:ring-[#2A5A3B]"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="q-phone" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {t.quote.labels.phone}
              </Label>
              <Input
                id="q-phone"
                data-testid="quote-input-phone"
                type="tel"
                required
                placeholder={t.quote.placeholders.phone}
                value={form.phone}
                onChange={update("phone")}
                className="mt-2 rounded-none border-earth focus-visible:ring-[#2A5A3B]"
              />
            </div>
            <div>
              <Label htmlFor="q-service" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
                {t.quote.labels.service}
              </Label>
              <Select
                value={form.service}
                onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}
              >
                <SelectTrigger
                  id="q-service"
                  data-testid="quote-select-service"
                  className="mt-2 rounded-none border-earth focus:ring-[#2A5A3B]"
                >
                  <SelectValue placeholder={t.quote.placeholders.service} />
                </SelectTrigger>
                <SelectContent>
                  {t.quote.services.map((s) => (
                    <SelectItem
                      key={s}
                      value={s}
                      data-testid={`quote-service-option-${s.replace(/\s+/g, "-").toLowerCase()}`}
                    >
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="q-address" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
              {t.quote.labels.address}
            </Label>
            <div className="relative mt-2">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="q-address"
                data-testid="quote-input-address"
                type="text"
                required
                placeholder={t.quote.placeholders.address}
                value={form.address}
                onChange={update("address")}
                className="rounded-none border-earth focus-visible:ring-[#2A5A3B] pl-9"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="q-message" className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
              {t.quote.labels.message}
            </Label>
            <Textarea
              id="q-message"
              data-testid="quote-input-message"
              rows={4}
              placeholder={t.quote.placeholders.message}
              value={form.message}
              onChange={update("message")}
              className="mt-2 rounded-none border-earth focus-visible:ring-[#2A5A3B]"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="quote-submit-button"
            className="inline-flex items-center justify-center gap-2 bg-[#C05C3F] hover:bg-[#a94c32] text-white font-medium px-8 py-4 w-full sm:w-auto transition-colors rounded-none disabled:opacity-60"
          >
            {submitting ? t.quote.submitting : t.quote.submit}
            {!submitting && <ArrowRight className="h-4 w-4" />}
          </button>
        </form>
      </div>
    </section>
  );
};
